import { ATTRIBUTE_RATINGS } from '../../types';
import type { AttributeKey, AttributeRating } from '../../types';

/**
 * §17.2 — the **four-state** segmented rating control: `—` · Great · OK · Needs work.
 *
 * ── Why four states and not three-plus-a-hidden-clear ────────────────────────
 *
 * §3.7.37 makes *not assessed* a legitimate **final** state, not a not-yet. If clearing is harder
 * than setting, a wrong rating stays — and that corrupts the one input this whole amendment exists
 * to provide. So the unset state is a first-class segment, selected by default, and **tapping the
 * currently-selected segment clears back to unset**.
 *
 * ── The unset segment's label is load-bearing ───────────────────────────────
 *
 * An em dash and `aria-label="Not assessed"` — **never** a word like *Average* or *None*. Naming
 * the blank would give absence a second meaning at the exact surface where §3.7.37 says it has
 * only one, and the renderer's whole contract (*absence is silence*) is downstream of what the
 * user believed they were choosing here.
 *
 * Selection is never hue-only: filled background, weight change, and a check glyph.
 */

/** §3.7.37's ratings as the control's segment captions. Exhaustive `Record` — a fourth rating
 *  without a caption is a compile error rather than a segment with no label. */
const RATING_CAPTION: Readonly<Record<AttributeRating, string>> = {
  great: 'Great',
  okay: 'OK',
  needsWork: 'Needs work',
};

export interface RatingControlProps {
  readonly attributeKey: AttributeKey;
  readonly label: string;
  /** `undefined` = not assessed, and that is a value the user can deliberately return to. */
  readonly value: AttributeRating | undefined;
  readonly disabled?: boolean;
  /** `undefined` clears. */
  readonly onChange: (rating: AttributeRating | undefined) => void;
}

export function RatingControl({
  attributeKey,
  label,
  value,
  disabled = false,
  onChange,
}: RatingControlProps) {
  return (
    <div className="myteam-rating" data-testid={`myteam-rating-${attributeKey}`}>
      <span className="myteam-rating__label" id={`myteam-rating-label-${attributeKey}`}>
        {label}
      </span>
      <div
        className="myteam-rating__segments"
        role="group"
        aria-labelledby={`myteam-rating-label-${attributeKey}`}
        data-value={value ?? 'unset'}
      >
        <button
          type="button"
          className="myteam-seg"
          data-testid={`myteam-rating-${attributeKey}-unset`}
          aria-label="Not assessed"
          aria-pressed={value === undefined}
          disabled={disabled}
          onClick={() => {
            onChange(undefined);
          }}
        >
          —
        </button>
        {ATTRIBUTE_RATINGS.map((rating) => (
          <button
            key={rating}
            type="button"
            className="myteam-seg"
            data-testid={`myteam-rating-${attributeKey}-${rating}`}
            aria-pressed={value === rating}
            disabled={disabled}
            onClick={() => {
              // Clear-by-retap: pressing the selected segment returns to *not assessed*, which
              // §3.7.37 makes a final state rather than a gap.
              onChange(value === rating ? undefined : rating);
            }}
          >
            {RATING_CAPTION[rating]}
          </button>
        ))}
      </div>
    </div>
  );
}
